import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import mcqService from '../services/mcqService';
import LoadingSpinner from './ui/LoadingSpinner';
import ErrorMessage from './ui/ErrorMessage';

/**
 * MCQList - Component to display available MCQs for a course
 * Shows MCQ cards with question count, best score, and start button
 */
const MCQList = ({ courseId }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [mcqs, setMcqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (courseId) {
      fetchMCQs();
    }
  }, [courseId]);

  /**
   * Fetch MCQs for this course
   */
  const fetchMCQs = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mcqService.getAllMCQs(courseId);
      setMcqs(data);
    } catch (err) {
      console.error('Failed to fetch MCQs:', err);
      setError(err.response?.data?.detail || 'Failed to load MCQs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle start MCQ button click
   */
  const handleStartMCQ = (mcqId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(`/mcq/${mcqId}`);
  };

  // Loading state
  if (loading) {
    return <LoadingSpinner message="Loading MCQs..." />;
  }

  // Error state
  if (error) {
    return <ErrorMessage message={error} onRetry={fetchMCQs} />;
  }

  // No MCQs
  if (mcqs.length === 0) {
    return null; // Don't show anything if there are no MCQs
  }

  return (
    <div className="mt-12 mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">MCQ Tests</h2>

      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {mcqs.map((mcq) => {
          // MCQ is locked if user doesn't have access (not a preview AND no enrollment)
          const isLocked = !mcq.user_has_access;

          return (
            <div
              key={mcq.id}
              className={`bg-white rounded-lg shadow-md transition-shadow p-6 border border-gray-200 ${
                isLocked ? 'opacity-60' : 'hover:shadow-xl'
              }`}
            >
              {/* MCQ Header */}
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className={`text-xl font-bold flex-grow ${isLocked ? 'text-gray-500' : 'text-gray-900'}`}>
                    {mcq.title}
                  </h3>
                  {!isLocked ? (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full ml-2">
                      {mcq.is_preview ? 'Free' : 'Enrolled'}
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full ml-2 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Locked
                    </span>
                  )}
                </div>
                {mcq.description && (
                  <p className={`text-sm line-clamp-2 ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                    {mcq.description}
                  </p>
                )}
              </div>

              {/* MCQ Stats */}
              <div className={`flex items-center gap-4 mb-4 text-sm ${isLocked ? 'text-gray-400' : 'text-gray-600'}`}>
                <div className="flex items-center gap-1">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{mcq.question_count} Questions</span>
                </div>
                {mcq.user_best_score !== null && (
                  <div className="flex items-center gap-1 text-green-600 font-semibold">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Best: {Number(mcq.user_best_score).toFixed(0)}%</span>
                  </div>
                )}
              </div>

              {/* Enrollment Required Message */}
              {isLocked && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg flex items-start gap-2">
                  <svg className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <p className="text-xs text-gray-600">
                    <span className="font-semibold">Enrollment Required</span> - Purchase this course to access this MCQ
                  </p>
                </div>
              )}

              {/* Action Button */}
              <button
                onClick={() => handleStartMCQ(mcq.id)}
                disabled={isLocked}
                className={`w-full px-6 py-3 rounded-lg font-semibold shadow-lg transition-all ${
                  isLocked
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 hover:shadow-xl'
                }`}
              >
                {isLocked ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Enroll to Access
                  </span>
                ) : (
                  mcq.user_best_score !== null ? 'Retake MCQ' : 'Start MCQ'
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MCQList;
